import { Image } from '../../molecules'
import {
  AtButton,
  AtButtonProps,
  AtLink,
  AtLinkType,
  AtNavbarLink,
  AtNavbarLinkProps,
  Menu,
  NavArrowLeft,
} from '../../atoms'
import { MouseEvent, useEffect, useState } from 'react'
import { OrMobileMenu } from '../or-mobile-menu'
import { MlNavbarDropdown, MlNavbarDropdownProps, openDirectionType } from '../../molecules/ml-navbar-dropdown'

export interface OrNavbarProps {
  logo: Image
  links?: ((AtNavbarLinkProps & { type: 'navbarLink' }) | (MlNavbarDropdownProps & { type: 'dropdown' }))[]
  buttons?: AtButtonProps[]
  /** Setting this value will override every other element inside the navigation bar besides the logo, meant only for usage within the checkout flow */
  checkoutHref?: string
  onLogoClick?: (event?: MouseEvent<HTMLAnchorElement>) => void
  onBackClick?: (event?: MouseEvent<HTMLAnchorElement>) => void
}

export const OrNavbar = ({
  buttons,
  checkoutHref,
  links,
  logo: { alt, src },
  onLogoClick,
  onBackClick,
}: OrNavbarProps) => {
  const [open, setOpen] = useState(false)
  const [current, setCurrent] = useState('')

  useEffect(() => setCurrent(window.location.href), [])

  return (
    <>
      <header
        className="bg-neutrals-white border-b-1 border-neutrals-300 sticky top-0 z-50 lg:static"
        id="page-header"
        role="navigation"
        aria-label="Main Navigation"
      >
        <div
          className={`h-[4.5rem] lg:h-[6.625rem] lg:max-w-screen-lg mx-auto px-4 lg:px-10 flex items-center ${
            checkoutHref ? '' : 'justify-between'
          }`}
        >
          <a
            onClick={onLogoClick}
            href="/"
            className={`${
              checkoutHref ? 'hidden lg:block' : ''
            } focus:outline-none focus-visible:outline-offset-4 focus-visible:outline focus-visible:outline-1 focus-visible:outline-neutrals-900`}
          >
            <img src={src} alt={alt} width={1914} height={737} className="w-[4.5rem] lg:w-[8.5rem]" />
          </a>
          {links && !checkoutHref && (
            <nav className="hidden h-full lg:flex px-10">
              {links.map((link, i) => {
                if (link.type === 'dropdown') {
                  const size = links.length
                  let direction: openDirectionType = 'right'
                  if (size > 1) {
                    if (i === size - 1) {
                      direction = 'left'
                    } else if (size >= 4 && (i === size - 2 || (i === size - 3 && link.dropdownLinks.length > 12))) {
                      direction = 'left'
                    } else if (size >= 7 && (i === size - 3 || (i === size - 4 && link.dropdownLinks.length > 12))) {
                      direction = 'left'
                    }
                  }
                  return (
                    <MlNavbarDropdown
                      key={i}
                      title={link.title}
                      dropdownLinks={link.dropdownLinks}
                      openDirection={direction}
                    />
                  )
                }
                return (
                  <AtNavbarLink
                    key={i}
                    {...link}
                    selected={new RegExp(link.href).test(current)}
                    className={links[i + 1]?.type === 'navbarLink' ? 'mr-16 last:mr-0' : ''}
                  />
                )
              })}
            </nav>
          )}
          {buttons && !checkoutHref && (
            <div className="hidden lg:flex gap-6">
              {buttons.map((button, i) => (
                <AtButton key={i} {...button} parentName="OrNavbar" className="min-w-[11.25rem]" isLink />
              ))}
            </div>
          )}
          {!checkoutHref && (links || buttons) && (
            <button
              className="w-10 h-10 flex justify-center items-center lg:hidden"
              aria-label="Open navigation menu"
              onClick={() => setOpen(true)}
            >
              <Menu />
            </button>
          )}
          {checkoutHref && (
            <div className="lg:ml-12">
              <AtLink
                onClick={onBackClick}
                type={AtLinkType.SECONDARY}
                actionTarget={checkoutHref}
                className="text-14 lg:text-16"
                title="Back to Space Details"
                IconLeftRef={NavArrowLeft}
              />
            </div>
          )}
        </div>
      </header>
      {!checkoutHref && (
        <OrMobileMenu
          onLogoClick={onLogoClick}
          logo={{ alt, src }}
          open={open}
          onClose={() => setOpen(false)}
          links={links}
          buttons={buttons}
        />
      )}
    </>
  )
}
